import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "@digitalisation/enlixy-comp";
import { styles, cx } from "../styles";
import DashboardImporter, {
  type ImportedDashboard,
  type DashboardComponent,
  useResponsiveDashboard,
} from "./DashboardImporter";
import { useDashboardStore } from "../store/DashboardStore";
import PanelRenderer from "../dashboard/PanelRenderer";
import { DashboardSyncProvider } from "../dashboard/DashboardSyncContext";
import RangePicker from "../dashboard/RangePicker";
import { asDataBinding, type ResolvedRange } from "../dashboard/spec";
import "../dashboard"; // registers the data-source providers

interface DashboardProps {
  machineId?: string;
  machineName?: string;
  machineData?: any;
  dashboardId: string;
  dashboardName: string;
}

/**
 * Default range for imported dashboards until they gain their own date picker:
 * live. NATS and Hybrid panels work immediately; pure-Doris panels need a
 * window (added with the picker).
 */
const IMPORTED_RANGE: ResolvedRange = { kind: "live" };

/** Placeholder body used when a component has no data binding. */
const PlaceholderBody: React.FC<{ component: DashboardComponent }> = ({
  component,
}) => {
  const icon =
    component.type === "chart"
      ? "📊"
      : component.type === "kpi"
        ? "🔢"
        : component.type === "gauge"
          ? "⚡"
          : component.type === "table"
            ? "📋"
            : component.type === "text"
              ? "🏷️"
              : "";
  if (component.type === "label") {
    return (
      <div
        className="w-full h-full flex items-center justify-center p-2 text-center overflow-hidden"
        style={{
          fontSize: component.props.fontSize || 14,
          color: component.props.color || "var(--text-primary)",
          wordBreak: "break-word",
        }}
      >
        {component.props.text || "Label"}
      </div>
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center p-2">
      <div className="text-center">
        <div className="text-2xl mb-2">{icon}</div>
        <div
          className="text-sm font-medium"
          style={{ color: "var(--text-primary)" }}
        >
          {component.props.title || component.type}
        </div>
      </div>
    </div>
  );
};

/** Renders a component: a live data-bound panel when bound, else the placeholder. */
const ImportedComponentBody: React.FC<{
  component: DashboardComponent;
  range: ResolvedRange;
  machineBK?: string;
}> = ({ component, range, machineBK }) => {
  if (asDataBinding(component.props?.dataBinding)) {
    return (
      <PanelRenderer
        component={component}
        range={range}
        machineBK={machineBK}
        fallback={<PlaceholderBody component={component} />}
      />
    );
  }
  return <PlaceholderBody component={component} />;
};

const defaultDashboardState = {
  importedDashboard: null,
  isPreviewMode: false,
  hideHeader: false,
} as const;

const Dashboard: React.FC<DashboardProps> = ({
  machineId,
  machineName,
  dashboardId,
  dashboardName,
}) => {
  const toast = useToast();
  // Zustand store selectors
  const dashboardState = useDashboardStore((s) => s.dashboards[dashboardId]);
  const setDashboardState = useDashboardStore((s) => s.setDashboardState);
  const resetDashboardState = useDashboardStore((s) => s.resetDashboardState);
  const removeDashboard = useDashboardStore((s) => s.removeDashboard);

  // Check if store has been hydrated from localStorage
  const hasHydrated = useDashboardStore((s) => s.dashboards !== undefined);

  // Responsive dashboard utilities
  const responsive = useResponsiveDashboard();

  // Dashboard-level time range for data-bound panels (live / window).
  const [dashRange, setDashRange] = useState<ResolvedRange>(IMPORTED_RANGE);

  // Use memoized state to prevent unnecessary re-renders
  const state = useMemo(
    () => dashboardState || defaultDashboardState,
    [dashboardState],
  );

  // Ensure dashboard state is initialized only once per dashboardId
  useEffect(() => {
    if (hasHydrated && !useDashboardStore.getState().dashboards[dashboardId]) {
      setDashboardState(dashboardId, defaultDashboardState);
    }
  }, [dashboardId, setDashboardState, hasHydrated]);

  // Show loading state while hydrating
  if (!hasHydrated) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Handle dashboard import
  const handleDashboardImport = (dashboard: ImportedDashboard) => {
    setDashboardState(dashboardId, {
      importedDashboard: dashboard,
      isPreviewMode: true,
      hideHeader: false,
    });
  };

  const handleImportError = (error: string) => {
    console.error("Dashboard import error:", error);
    toast.error(error, "Error importing dashboard");
  };

  const handleApplyDashboard = () => {
    setDashboardState(dashboardId, {
      isPreviewMode: false,
      hideHeader: true,
    });
    // Here you could save the dashboard to the user's collection
    console.log("Dashboard applied:", state.importedDashboard);
  };

  const handleRemoveDashboard = () => {
    resetDashboardState(dashboardId);
    removeDashboard(dashboardId);
  };

  // Dashboard options panel for new/empty dashboards
  // ToolboxButton component for consistent dashboard action buttons
  type ToolboxButtonProps = {
    icon: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    type?: "button" | "submit" | "reset";
  };
  const ToolboxButton: React.FC<ToolboxButtonProps> = ({
    icon,
    children,
    className,
    ...props
  }) => (
    <button
      className={cx(
        "flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors w-full",
        className,
      )}
      {...props}
    >
      <span>{icon}</span>
      {children}
    </button>
  );

  function DashboardOptionsPanel() {
    return (
      <div className={styles.dashboard.options.container}>
        <div className="text-center mb-8">
          <h3 className={styles.dashboard.options.title}>{dashboardName}</h3>
          <p
            className="text-sm opacity-70 mt-2"
            style={{
              color: "var(--text-secondary)",
              transition: "color 0.3s ease",
            }}
          >
            Machine: {machineName || "Unknown"} ({machineId || "N/A"})
          </p>
        </div>
        <div className={styles.dashboard.options.buttonGrid}>
          <ToolboxButton
            icon="➕"
            className={cx(styles.button.primary, styles.button.large)}
            onClick={() => toast.info("Add existing dashboard (not implemented)")}
          >
            Add Existing Dashboard
          </ToolboxButton>
          <ToolboxButton
            icon="🆕"
            className={cx(styles.button.success, styles.button.large)}
            onClick={() =>
              window.open(
                import.meta.env.VITE_EDITOR_URL || "https://editor.enlixy.com",
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            Create New Dashboard
          </ToolboxButton>
          <DashboardImporter
            onImport={handleDashboardImport}
            onError={handleImportError}
          >
            <ToolboxButton
              icon="📥"
              className={cx(styles.button.warning, styles.button.large)}
            >
              Import Dashboard
            </ToolboxButton>
          </DashboardImporter>
          <ToolboxButton
            icon="🤖"
            className={cx(styles.button.accent, styles.button.large)}
            onClick={() => toast.info("Generate with AI (not implemented)")}
          >
            Generate Dashboard with AI
          </ToolboxButton>
        </div>
        {/* Display imported dashboard info */}
        {state.importedDashboard && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4
              className="text-sm font-medium mb-2"
              style={{
                color: "var(--text-primary)",
                transition: "color 0.3s ease",
              }}
            >
              Imported Dashboard
            </h4>
            <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {state.importedDashboard.name}
              </p>
              {state.importedDashboard.description && (
                <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                  {state.importedDashboard.description}
                </p>
              )}
              <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                Components: {state.importedDashboard.config.components.length}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Dashboard Preview Panel - shows imported dashboard with Apply/Remove buttons
  function DashboardPreviewPanel() {
    if (!state.importedDashboard) return null;
    return (
      <div className="h-full flex flex-col">
        {/* Preview Header */}
        <div
          className="flex items-center justify-between p-4 border-b bg-yellow-50 dark:bg-yellow-900/20"
          style={{
            borderColor: "var(--border-primary)",
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
              Preview: {state.importedDashboard.name}
            </h3>
            {state.importedDashboard.description && (
              <span className="text-sm opacity-70 text-yellow-600 dark:text-yellow-300">
                - {state.importedDashboard.description}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              className={cx(
                "px-4 py-2 text-sm rounded-md transition-colors font-medium",
                "bg-green-600 text-white hover:bg-green-700",
              )}
              onClick={handleApplyDashboard}
            >
              ✓ Apply Dashboard
            </button>
            <button
              className={cx(
                "px-4 py-2 text-sm rounded-md transition-colors font-medium",
                "bg-red-600 text-white hover:bg-red-700",
              )}
              onClick={handleRemoveDashboard}
            >
              ✗ Remove
            </button>
          </div>
        </div>
        {/* Dashboard Content - Full Width, No Border, Transparent Background */}
        <div className="flex-1 p-6 overflow-auto">
          <div
            className="relative w-full"
            style={{
              backgroundColor: "transparent",
              border: "none",
              minHeight: 400,
              height: responsive.getLayoutDimensions(
                state.importedDashboard.config.layout,
              ).height,
              maxHeight: "100%",
            }}
          >
            {/* Render dashboard components */}
            {state.importedDashboard.config.components.map((component) => {
              const position = responsive.getComponentPosition(component);
              const size = responsive.getComponentSize(component);
              return (
                <div
                  key={component.id}
                  className="absolute rounded shadow-sm"
                  style={{
                    left: position.x,
                    top: position.y,
                    width: size.width,
                    height: size.height,
                    backgroundColor: "var(--bg-primary)",
                    transition:
                      "background-color 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  {/* Render component based on type */}
                  <ImportedComponentBody
                    component={component}
                    range={dashRange}
                    machineBK={machineId}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Dashboard View Panel - shows applied dashboard with full controls
  function DashboardViewPanel() {
    if (!state.importedDashboard) return null;
    return (
      <div className="h-full flex flex-col">
        {/* Only show header if not in minimal view (after Apply) */}
        {!state.hideHeader && (
          <div
            className="flex items-center justify-between p-4 border-b"
            style={{
              borderColor: "var(--border-primary)",
              backgroundColor: "var(--bg-primary)",
              transition: "background-color 0.3s ease, border-color 0.3s ease",
            }}
          >
            <div className="flex items-center gap-3">
              <h3
                className="font-medium"
                style={{
                  color: "var(--text-primary)",
                  transition: "color 0.3s ease",
                }}
              >
                {state.importedDashboard.name}
              </h3>
              {state.importedDashboard.description && (
                <span
                  className="text-sm opacity-70"
                  style={{
                    color: "var(--text-secondary)",
                    transition: "color 0.3s ease",
                  }}
                >
                  - {state.importedDashboard.description}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <RangePicker value={dashRange} onChange={setDashRange} />
              <button
                className={cx(
                  "px-3 py-1.5 text-xs rounded-md transition-colors",
                  "bg-red-600 text-white hover:bg-red-700",
                )}
                onClick={handleRemoveDashboard}
              >
                🗑️ Remove
              </button>
            </div>
          </div>
        )}
        {/* Dashboard Content - Full Width, No Border, Transparent Background */}
        <div className="flex-1 p-6 overflow-auto">
          <div
            className="relative w-full"
            style={{
              backgroundColor: "transparent",
              border: "none",
              minHeight: 400,
              height: responsive.getLayoutDimensions(
                state.importedDashboard.config.layout,
              ).height,
              maxHeight: "100%",
            }}
          >
            {/* Render dashboard components */}
            {state.importedDashboard.config.components.map((component) => {
              const position = responsive.getComponentPosition(component);
              const size = responsive.getComponentSize(component);
              return (
                <div
                  key={component.id}
                  className="absolute rounded shadow-sm"
                  style={{
                    left: position.x,
                    top: position.y,
                    width: size.width,
                    height: size.height,
                    backgroundColor: "var(--bg-primary)",
                    transition:
                      "background-color 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  {/* Render component based on type */}
                  <ImportedComponentBody
                    component={component}
                    range={dashRange}
                    machineBK={machineId}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <DashboardSyncProvider>
      <div className="h-full">
        {state.importedDashboard && state.isPreviewMode ? (
          <DashboardPreviewPanel />
        ) : state.importedDashboard ? (
          <DashboardViewPanel />
        ) : (
          <DashboardOptionsPanel />
        )}
      </div>
    </DashboardSyncProvider>
  );
};

export default Dashboard;
